import { createContext, useContext, useState, useEffect } from "react";
import { Global } from "../constants/global";

const CartContext = createContext({});

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const savedCart = localStorage.getItem(Global.JIGENPASS_B2B_CART_COOKIE);
    return savedCart ? JSON.parse(savedCart) : [];
  });

  useEffect(() => {
    localStorage.setItem(
      Global.JIGENPASS_B2B_CART_COOKIE,
      JSON.stringify(cartItems),
    );
  }, [cartItems]);

  const calculateItemPrice = (product, quantity) => {
    if (!product.bulkPricing || product.bulkPricing.length === 0) {
      return product.wholesalePrice || 0;
    }

    const applicableTier = [...product.bulkPricing]
      .sort((a, b) => b.minQty - a.minQty)
      .find((tier) => quantity >= tier.minQty);

    return applicableTier ? applicableTier.price : product.wholesalePrice || 0;
  };

  const addToCart = (product, quantity = 1) => {
    const qty = parseInt(quantity);
    const existing = cartItems.find((item) => item.sku === product.sku);
    const totalQty = (existing ? existing.quantity : 0) + qty;

    if (cartItems.length > 0) {
      const firstItem = cartItems[0];
      if (!!firstItem.isPreOrder !== !!product.isPreOrder) {
        throw new Error(
          "Cannot mix Pre-order and In-Stock items in a single cart. Please create separate orders.",
        );
      }
    }

    const minQty = product.minPurchaseQty || 1;
    if (totalQty < minQty) {
      throw new Error(`Minimum purchase quantity is ${minQty}.`);
    }

    if (product.maxPurchaseQty > 0 && totalQty > product.maxPurchaseQty) {
      throw new Error(
        `Maximum purchase quantity is ${product.maxPurchaseQty}.`,
      );
    }

    if (product.isPreOrder) {
      const totalQuota = parseInt(product.preOrderQuota) || 0;
      if (totalQuota > 0) {
        const available = totalQuota - (product.totalSales || 0);
        if (totalQty > available) {
          throw new Error(
            `Only ${Math.max(0, available)} units available for pre-order quota.`,
          );
        }
      }
    } else {
      if (product.manageStock && totalQty > (product.stockQuantity || 0)) {
        throw new Error(
          `Only ${Math.max(0, product.stockQuantity || 0)} units available in stock.`,
        );
      }
    }

    setCartItems((prev) => {
      const existingItem = prev.find((item) => item.sku === product.sku);
      if (existingItem) {
        return prev.map((item) =>
          item.sku === product.sku
            ? { ...item, quantity: item.quantity + qty }
            : item,
        );
      }
      return [...prev, { ...product, quantity: qty }];
    });
  };

  const addMultipleToCart = (items) => {
    setCartItems((prev) => {
      const newItems = [...prev];
      items.forEach(({ product, quantity }) => {
        const existingIndex = newItems.findIndex(
          (item) => item.sku === product.sku,
        );
        if (existingIndex > -1) {
          newItems[existingIndex] = {
            ...newItems[existingIndex],
            quantity: newItems[existingIndex].quantity + parseInt(quantity),
          };
        } else {
          newItems.push({ ...product, quantity: parseInt(quantity) });
        }
      });
      return newItems;
    });
  };

  const updateQuantity = (sku, quantity) => {
    const qty = parseInt(quantity);
    if (qty <= 0) {
      setCartItems((prev) => prev.filter((item) => item.sku !== sku));
      return;
    }

    const product = cartItems.find((item) => item.sku === sku);
    if (!product) return;

    const minQty = product.minPurchaseQty || 1;
    if (qty < minQty) {
      throw new Error(`Minimum purchase quantity is ${minQty}.`);
    }

    if (product.maxPurchaseQty > 0 && qty > product.maxPurchaseQty) {
      throw new Error(
        `Maximum purchase quantity is ${product.maxPurchaseQty}.`,
      );
    }

    if (product.isPreOrder) {
      const totalQuota = parseInt(product.preOrderQuota) || 0;
      if (totalQuota > 0) {
        const available = totalQuota - (product.totalSales || 0);
        if (qty > available) {
          throw new Error(
            `Only ${Math.max(0, available)} units available for pre-order quota.`,
          );
        }
      }
    } else {
      if (product.manageStock && qty > (product.stockQuantity || 0)) {
        throw new Error(
          `Only ${Math.max(0, product.stockQuantity || 0)} units available in stock.`,
        );
      }
    }

    setCartItems((prev) =>
      prev.map((item) =>
        item.sku === sku ? { ...item, quantity: qty } : item,
      ),
    );
  };

  const removeFromCart = (sku) => {
    setCartItems((prev) => prev.filter((item) => item.sku !== sku));
  };

  const clearCart = () => setCartItems([]);

  const cartTotal = cartItems.reduce((acc, item) => {
    const price = calculateItemPrice(item, item.quantity);
    return acc + price * item.quantity;
  }, 0);

  const cartCount = cartItems.reduce((acc, item) => acc + item.quantity, 0);

  const value = {
    cartItems,
    addToCart,
    addMultipleToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    cartTotal,
    cartCount,
    calculateItemPrice,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
